import React, { useState, useEffect } from "react";
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Chip, Button, Dialog,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useGetAmMrSubmissionsMutation } from "state/api";
import { toast } from "react-toastify";

const AmDetails = () => {
  const { mrMgCd } = useParams();
  const { user } = useSelector((s) => s.auth);
  const navigate = useNavigate();
  const [getAmMrSubmissions] = useGetAmMrSubmissionsMutation();
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ mr: {}, rx: [], weekly: [], psp: [], lycored: [] });
  const [loading, setLoading] = useState(true);
  const [imgPreview, setImgPreview] = useState(null);
  const BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    if (user?.role && mrMgCd) {
      setLoading(true);
      getAmMrSubmissions({ MgCd: user.role, mrMgCd })
        .unwrap()
        .then(setData)
        .catch((err) => toast.error(err?.data?.message || "Failed to load"))
        .finally(() => setLoading(false));
    }
  }, [user, mrMgCd, getAmMrSubmissions]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button onClick={() => navigate("/am/mr-list")} sx={{ mb: 1 }}>&larr; Back to MR List</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {data.mr?.name || mrMgCd} — Submissions
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        EmpNo: {data.mr?.EmpNo || "N/A"} | MgCd: {data.mr?.MgCd || mrMgCd}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Rx (${data.rx?.length || 0})`} />
        <Tab label={`Weekly (${data.weekly?.length || 0})`} />
        <Tab label={`PSP (${data.psp?.length || 0})`} />
        <Tab label={`Lycored (${data.lycored?.length || 0})`} />
      </Tabs>

      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Doctor</TableCell><TableCell>Category</TableCell><TableCell>Visit Date</TableCell>
              <TableCell>New Rx</TableCell><TableCell>Samples</TableCell><TableCell>POB</TableCell><TableCell>Rx Photo</TableCell><TableCell>POB Invoice</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.rx || []).map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.DoctName}</TableCell>
                  <TableCell>{r.DocCategory || "-"}</TableCell>
                  <TableCell>{r.visit_date?.split("T")[0]}</TableCell>
                  <TableCell>{r.new_rx_count}</TableCell>
                  <TableCell>{r.folireg_samples_given}</TableCell>
                  <TableCell>{r.pob_strips}</TableCell>
                  <TableCell>{r.rx_photo ? <Box component="img" src={`${BASE}${r.rx_photo}`} sx={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer", borderRadius: 1 }} onClick={() => setImgPreview(`${BASE}${r.rx_photo}`)} /> : "-"}</TableCell>
                  <TableCell>{r.pob_invoice ? <Box component="img" src={`${BASE}${r.pob_invoice}`} sx={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer", borderRadius: 1 }} onClick={() => setImgPreview(`${BASE}${r.pob_invoice}`)} /> : "-"}</TableCell>
                  <TableCell>{r.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {(!data.rx || data.rx.length === 0) && <TableRow><TableCell colSpan={9} align="center">No submissions</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Week Start</TableCell><TableCell>Chemist Stock</TableCell><TableCell>Total Strips</TableCell>
              <TableCell>POB Strips</TableCell><TableCell>Prescribers</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.weekly || []).map((w) => (
                <TableRow key={w.id}>
                  <TableCell>{w.week_start_date?.split("T")[0]}</TableCell>
                  <TableCell>{w.chemist_stock_count}</TableCell>
                  <TableCell>{w.total_strips_at_chemist}</TableCell>
                  <TableCell>{w.pob_taken_strips}</TableCell>
                  <TableCell>{w.prescriber_count}</TableCell>
                  <TableCell>{w.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {(!data.weekly || data.weekly.length === 0) && <TableRow><TableCell colSpan={6} align="center">No submissions</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Doctor</TableCell><TableCell>PSP Kits</TableCell><TableCell>Date Given</TableCell>
              <TableCell>New Rx w/ PSP</TableCell><TableCell>Free Strips</TableCell><TableCell>Rx Photo</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.psp || []).map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.DoctName}</TableCell>
                  <TableCell>{p.psp_kits_given}</TableCell>
                  <TableCell>{p.date_given?.split("T")[0]}</TableCell>
                  <TableCell>{p.new_rx_with_psp}</TableCell>
                  <TableCell>{p.free_strips_given}</TableCell>
                  <TableCell>{p.rx_photo ? <Box component="img" src={`${BASE}${p.rx_photo}`} sx={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer", borderRadius: 1 }} onClick={() => setImgPreview(`${BASE}${p.rx_photo}`)} /> : "-"}</TableCell>
                  <TableCell>{p.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {(!data.psp || data.psp.length === 0) && <TableRow><TableCell colSpan={7} align="center">No submissions</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 3 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Doctor</TableCell><TableCell>IVF</TableCell><TableCell>Potential</TableCell>
              <TableCell>Available</TableCell><TableCell>Strips</TableCell><TableCell>Bonanza</TableCell><TableCell>Prescription</TableCell><TableCell>Date</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {(data.lycored || []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.DoctName}</TableCell>
                  <TableCell><Chip size="small" label={l.practicing_in_ivf ? "Yes" : "No"} color={l.practicing_in_ivf ? "success" : "default"} /></TableCell>
                  <TableCell>{l.monthly_potential}</TableCell>
                  <TableCell><Chip size="small" label={l.lycored_m_available ? "Yes" : "No"} color={l.lycored_m_available ? "success" : "default"} /></TableCell>
                  <TableCell>{l.strips_available}</TableCell>
                  <TableCell><Chip size="small" label={l.bonanza_given ? "Yes" : "No"} color={l.bonanza_given ? "info" : "default"} /></TableCell>
                  <TableCell>{l.prescription_file ? <Box component="img" src={`${BASE}${l.prescription_file}`} sx={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer", borderRadius: 1 }} onClick={() => setImgPreview(`${BASE}${l.prescription_file}`)} /> : "-"}</TableCell>
                  <TableCell>{l.submitted_at?.split("T")[0]}</TableCell>
                </TableRow>
              ))}
              {(!data.lycored || data.lycored.length === 0) && <TableRow><TableCell colSpan={8} align="center">No submissions</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!imgPreview} onClose={() => setImgPreview(null)} maxWidth="md">
        <Box sx={{ p: 1 }}>
          <Box component="img" src={imgPreview} sx={{ maxWidth: "100%", maxHeight: "80vh" }} />
        </Box>
      </Dialog>
    </Box>
  );
};

export default AmDetails;
